import * as Yup from 'yup';

import { Button, Form, Spinner } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { black, dark, light } from '../../theme/colors';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';

import { Auth } from 'aws-amplify';
import ErrorLabel from '../../ui/ErrorLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoopLogoBlack from '../../assets/svg/loop-logo-black.svg';
import { RadioWrapper } from '../../ui/Styled';
import ReactMarkdown from 'react-markdown';
import SunsaveLogo from '../../assets/images/sunsave-logo.png';
import checkDisc from '../../assets/svg/disc/check-disc.svg';
import checkboxEmpty from '../../assets/svg/check-box-empty.svg';
import checkboxError from '../../assets/svg/check-box-error.svg';
import checkboxFilled from '../../assets/svg/check-box-filled.svg';
import { contentfulPropTypes } from '../../propTypes';
import cx from 'classnames';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import getAddressFromId from '../../utils/getAddressFromId';
import parseAddress from '../../utils/parseAddress';
import { registerSolarInterestInHubspot } from '../../api/Hubspot';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledAnchor = styled.a`
  text-decoration: none !important;
  font-weight: 800;
`;

const StyledHR = styled.hr`
  display: inline;
  height: 60px;
  width: 1px;
  background: ${dark};
  border-top-color: ${dark};
  margin: 12px 0 0 12px;
  padding: 0;
`;

const StyledReactMarkdown = styled(ReactMarkdown)`
  h2 {
    color: ${black};
  }
  p {
    a {
      text-decoration: underline !important;
    }
  }
`;

const StyledSunsaveLogo = styled.img`
  width: 266px;
  margin-right: -10px;
`;

const StyledRadioWrapper = styled(RadioWrapper)`
  border: 1px solid ${light};
  .form-check {
    background-position-y: 2px !important;
  }
`;

const StyledCheckboxWrapper = styled.div`
  p:last-child {
    margin-bottom: 0;
  }

  ul {
    list-style: none;
    padding-left: 18px;

    li {
      margin-bottom: 8px;

      &::before {
        content: '\\2022';
        color: #ff0085;
        font-weight: bold;
        display: inline-block;
        width: 1em;
        margin-left: -1em;
        font-size: 20px;
      }
    }
  }

  &.terms-div {
    cursor: pointer;
  }

  .form-check {
    padding-left: 0;
    background: url(${checkboxEmpty}) no-repeat top left;

    input {
      display: none;
    }
    .form-check-label {
      p {
        padding-left: 32px;
        font-size: 0.85rem;
      }
    }
  }

  &.selected {
    .form-check {
      background-image: url(${checkboxFilled});
    }
  }
  &.error {
    .form-check {
      background-image: url(${checkboxError});
    }
  }

  .error-label {
    margin-left: 32px;
  }
`;

const interestValues = {
  SOLAR_WITH_BATTERY: 'SOLAR_WITH_BATTERY',
  BATTERY_ONLY: 'BATTERY_ONLY',
};

const interestValueText = {
  SOLAR_WITH_BATTERY: "I'm interested in solar or solar with a battery",
  BATTERY_ONLY: "I'm interested in just a battery",
};

const phoneRegExp = /^0[0-9]{9,10}$/;

const schema = Yup.object().shape({
  phoneNumber: Yup.string()
    .matches(phoneRegExp, 'Phone number is not valid')
    .required('Please add your phone number'),
  solarInterest: Yup.mixed()
    .oneOf([interestValues.BATTERY_ONLY, interestValues.SOLAR_WITH_BATTERY])
    .required(),
  solarPartnerConsent: Yup.boolean().when('solarInterest', {
    is: interestValues.SOLAR_WITH_BATTERY,
    then: Yup.boolean().required('Please give consent to continue'),
  }),
  batteryInstallerConsent: Yup.boolean().when('solarInterest', {
    is: interestValues.BATTERY_ONLY,
    then: Yup.boolean().required('Please give consent to continue'),
  }),
});

const validateValues = values => {
  // phone number and solar interest are required
  if (!values.phoneNumber || !values.solarInterest) {
    return false;
  }

  // if solar interest is solar with battery then solar partner consent is required
  if (
    values.solarInterest === interestValues.SOLAR_WITH_BATTERY &&
    !values.solarPartnerConsent
  ) {
    return false;
  }

  // if solar interest is battery only then battery installer consent is required
  if (
    values.solarInterest === interestValues.BATTERY_ONLY &&
    !values.batteryInstallerConsent
  ) {
    return false;
  }

  return true;
};

const FormContainer = ({ loading, fetched, contentful }) => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(undefined);

  const onSubmit = values => {
    setSubmitting(true);
    Auth.currentAuthenticatedUser().then(res => {
      const parsedAddress = parseAddress(res.attributes.address);
      registerSolarInterestInHubspot(
        contentful.link,
        res.attributes.given_name,
        res.attributes.family_name,
        res.attributes.email,
        values.phoneNumber,
        getAddressFromId(res.attributes.address),
        parsedAddress.town,
        parsedAddress.postCode,
        interestValueText[values.solarInterest],
        values.solarPartnerConsent,
        values.batteryInstallerConsent,
      ).then(res => {
        setSubmitting(false);
        if (res) {
          setSubmitted(true);
          return true;
        } else {
          setError(true);
          return false;
        }
      });
    });
  };

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.SUNSAVE_SOLAR_FORM);
    };
    asyncRecordFlowProgress();
  }, []);

  return (
    !loading &&
    fetched && (
      <>
        {!submitted ? (
          <>
            <div className="text-left mb-2">
              <StyledAnchor href="/">
                <FontAwesomeIcon
                  size="lg"
                  className="em-text color-pink"
                  icon={faChevronLeft}
                />
                <span className="pl-2">Back</span>
              </StyledAnchor>
            </div>
            <div className="d-flex justify-content-between mb-3">
              <img src={LoopLogoBlack} alt="Loop Energy Logo" />
              <StyledHR />
              <StyledSunsaveLogo src={SunsaveLogo} alt="Loop Energy Logo" />
            </div>
            <h1 className="pb-3">{contentful?.title}</h1>
            <p>{contentful?.subtitle}</p>
            <StyledReactMarkdown className="pb-4 text-decoration-underline">
              {contentful?.body}
            </StyledReactMarkdown>

            <Formik
              onSubmit={onSubmit}
              initialValues={{
                phoneNumber: undefined,
                solarInterest: undefined,
                solarPartnerConsent: false,
                batteryInstallerConsent: false,
              }}
              validationSchema={schema}>
              {({ handleSubmit, values }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="text-left">
                    <Form.Label>Your phone number</Form.Label>
                  </div>
                  <Form.Group>
                    <Field name="phoneNumber">
                      {({ field, meta }) => (
                        <>
                          <Form.Control
                            name={field.name}
                            placeholder="Your phone number"
                            aria-label="Your phone number"
                            aria-describedby="basic-addon1"
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            className={cx({
                              error: !!meta.touched && !!meta.error,
                            })}
                          />
                          {meta.touched && meta.error && (
                            <ErrorLabel label={meta.error} />
                          )}
                        </>
                      )}
                    </Field>
                  </Form.Group>
                  <div className="text-left">
                    <Form.Label>
                      Are you interested in solar (with or without a battery) or
                      just a battery?
                    </Form.Label>
                    <Field name="solarInterest">
                      {({ field, meta }) => (
                        <StyledRadioWrapper
                          className={cx({
                            selected:
                              field.value === interestValues.SOLAR_WITH_BATTERY,
                          })}>
                          <Form.Check
                            type="radio"
                            name={field.name}
                            id="solar-interest-solar-with-solar"
                            value={interestValues.SOLAR_WITH_BATTERY}
                            label={
                              "I'm interested in solar, or solar with a battery"
                            }
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            className={cx({ error: !!meta.error })}
                          />
                        </StyledRadioWrapper>
                      )}
                    </Field>
                    <Field name="solarInterest">
                      {({ field, meta }) => (
                        <StyledRadioWrapper
                          className={cx({
                            selected:
                              field.value === interestValues.BATTERY_ONLY,
                          })}>
                          <Form.Check
                            type="radio"
                            name={field.name}
                            id="solar-interest-battery-only"
                            value={interestValues.BATTERY_ONLY}
                            label={
                              "I'm interested in just a battery - I have solar installed already, " +
                              'with no room for additional panels'
                            }
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            className={cx({ error: !!meta.error })}
                          />
                        </StyledRadioWrapper>
                      )}
                    </Field>
                    {values.solarInterest ===
                      interestValues.SOLAR_WITH_BATTERY && (
                      <Field name="solarPartnerConsent">
                        {({ field, meta }) => (
                          <StyledCheckboxWrapper
                            className={`text-left ${cx({
                              selected: field.value,
                              error: !!meta.error,
                            })}`}>
                            <Form.Check
                              type="checkbox"
                              name="solarPartnerConsent"
                              id="solar-partner-check"
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              className={cx({ error: !!meta.error })}
                              label={
                                <ReactMarkdown>
                                  {contentful?.bodyExtra}
                                </ReactMarkdown>
                              }
                            />
                            {meta.error && meta.touched && (
                              <ErrorLabel label={meta.error} />
                            )}
                          </StyledCheckboxWrapper>
                        )}
                      </Field>
                    )}
                    {values.solarInterest === interestValues.BATTERY_ONLY && (
                      <Field name="batteryInstallerConsent">
                        {({ field, meta }) => (
                          <StyledCheckboxWrapper
                            className={`text-left ${cx({
                              selected: field.value,
                              error: !!meta.error,
                            })}`}>
                            <Form.Check
                              type="checkbox"
                              name="batteryInstallerConsent"
                              id="battery-installer-check"
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              className={cx({ error: !!meta.error })}
                              label={
                                <ReactMarkdown>
                                  {contentful?.bodyEndExtra}
                                </ReactMarkdown>
                              }
                            />
                            {meta.error && meta.touched && (
                              <ErrorLabel label={meta.error} />
                            )}
                          </StyledCheckboxWrapper>
                        )}
                      </Field>
                    )}
                    {error && (
                      <div className="mt-4">
                        <ErrorLabel label="Something went wrong, please try again or contact support." />
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={submitting || !validateValues(values)}
                      className="w-100">
                      {!submitting ? (
                        contentful?.primaryButtonText
                      ) : (
                        <Spinner animation="border" size="sm" />
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <div>
            <p>
              <img src={checkDisc} alt="Check icon" />
            </p>
            {contentful?.bodyEnd && (
              <StyledReactMarkdown>{contentful.bodyEnd}</StyledReactMarkdown>
            )}
            <Button onClick={() => navigate(`/account/overview`)} block>
              {contentful?.secondaryButtonText}
            </Button>
          </div>
        )}
      </>
    )
  );
};

FormContainer.propTypes = {
  ...contentfulPropTypes,
};

export default withContentfulTag(FormContainer, 'web_app_solar_form_page');
